import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { CartContext } from './CartContext';
import Navbar from './components/Navbar';
import LoginPage from './components/LoginPage';
import RegistrationPage from './components/RegistrationPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import ProductListPage from './components/ProductListPage';
import ShoppingCartPage from './components/ShoppingCartPage';
import OrderPage from './components/OrderPage';
import ForgotPasswordEmailForm from './components/ForgotPasswordEmailForm';
import OrderConfirmationPage from './components/OrderConfirmationPage';
import HomePage from './components/HomePage';
import ProfilePage from './components/Profile';
import OrderHistoryPage from './components/OrderHistoryPage';

export default function App() {
  const [page, setPage] = useState(() => {
    // Check if user data exists - if not, default to login
    const userName = localStorage.getItem('userName');
    const customer = localStorage.getItem('customer');
    
    if (!userName || !customer) {
      return 'home';
    }
    
    const stored = localStorage.getItem('page');
    try {
      return stored ? JSON.parse(stored) : 'home';
    } catch {
      return stored || 'home';
    }
  });

  const [user, setUser] = useState(null);
  const API_BASE_URL = window._config?.apiBaseUrl || '';

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const customer = localStorage.getItem('customer');
    const cusAddress = localStorage.getItem('cusAddress');
    const mail = localStorage.getItem('mail');

    if (userName && customer) {
      setUser({
        
        name: userName,
        customer: customer,
        address: cusAddress || ''
      });
    }

    const bankName = localStorage.getItem('bankName');
    if (bankName) {
      setBank({
        accno: localStorage.getItem('accno') || '',
        bankName,
        bid: parseInt(localStorage.getItem('bid')) || 0,
        branch: localStorage.getItem('branch') || '',
        ifscode: localStorage.getItem('ifscode') || ''
      });
    }

    const qrcodelink = localStorage.getItem('qrcodelink');
    if (qrcodelink) {
      setQr({ qrcodelink });
    }
  }, []);

  const [bank, setBank] = useState(null);
  const [qr, setQr] = useState(null);
  const [cart, setCart] = useState(() => {
    const storedCart = localStorage.getItem('cart');
    try {
      return storedCart ? JSON.parse(storedCart) : [];
    } catch {
      return [];
    }
  });
  const [userEmail, setUserEmail] = useState('');

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);


  useEffect(() => {
    localStorage.setItem('page', typeof page === 'string' ? page : JSON.stringify(page));
  }, [page]);

  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        // Remove item if quantity becomes 0 or less
        if (newQuantity <= 0) {
          return prevCart.filter(item => item.id !== product.id);
        }
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      }
      // Don't add new item with 0 or negative quantity
      if (quantity <= 0) {
        return prevCart;
      }
      return [...prevCart, { ...product, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  }, [removeFromCart]);

  const clearCart = () => setCart([]);

  const cartContextValue = useMemo(() => ({
    cart,
    addToCart,
    updateQuantity,
    removeFromCart,
    itemCount: cart.length,
    totalQuantity: cart.reduce((sum, item) => sum + item.quantity, 0),
    totalPrice: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
  }), [cart, addToCart, updateQuantity, removeFromCart]);

  const handleRegister = (newUser) => {
    setUserEmail(newUser.email);
    setPage('verify-email');
  };

  const handleLogin = async (credentials, setError, setLoading) => {
    setLoading(true);
    setError('');
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (!ipResponse.ok) throw new Error('Could not fetch IP address.');
      const ipData = await ipResponse.json();
      const ipAddress = ipData.ip;

      const appKey = localStorage.getItem('appKey');
      const sessionId = '9944010155';
      const comId = 1;
      const { username,email, password } = credentials;

      const apiUrl = `${API_BASE_URL}/CheckCustomerLoginNew?databaseKey=${window._config?.databaseKey || ''}`;

      const loginResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          UsrName: email,
          UsrPwd: password,
          SessionId: sessionId,
          IPAddress: ipAddress,
          ComId: comId,
          AppKey: appKey
        })
      });

      if (!loginResponse.ok) {
        throw new Error(`API Error: ${loginResponse.status} - ${loginResponse.statusText}`);
      }

      const loginData = await loginResponse.json();
      if (loginData?.CheckCustomerLoginNewjs?.Table?.length > 0) {
        const userInfo = loginData.CheckCustomerLoginNewjs.Table[0];
        const BankInfo = loginData.CheckCustomerLoginNewjs.Table1?.[0] || {};
        const QrInfo = loginData.CheckCustomerLoginNewjs.Table2?.[0] || {};

        setUser({
           vid: userInfo.Usrid,
          name: userInfo.UsrName || username,
          customer: userInfo.Customer || '',
          address: userInfo.CusAddress || '',
          mail: userInfo.mail || ''
        });

        setBank({
          accno: BankInfo.AccNo || '',
          bankName: BankInfo.BankName || '',
          bid: BankInfo.Bid || 0,
          branch: BankInfo.Branch || '',
          ifscode: BankInfo.IFSCCode || ''
        });

        setQr({ qrcodelink: QrInfo.QRUPILINK || '' });

        localStorage.setItem('userName', userInfo.UsrName || username);
        localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('cusAddress', userInfo.CusAddress || '');
        localStorage.setItem('customer', userInfo.Customer || '');

        localStorage.setItem('vcode', userInfo.UsrName || '');
       

        localStorage.setItem('accno', BankInfo.AccNo || '');
        localStorage.setItem('bankName', BankInfo.BankName || '');
        localStorage.setItem('bid', BankInfo.Bid || '0');
        localStorage.setItem('branch', BankInfo.Branch || '');
        localStorage.setItem('ifscode', BankInfo.IFSCCode || '');
        localStorage.setItem('qrcodelink', QrInfo.QRUPILINK || '');

        localStorage.setItem('vname', userInfo.Customer || '');
        localStorage.setItem('vid', userInfo.Usrid || '0');
        localStorage.setItem('mail', userInfo.mail || '');
        sessionStorage.setItem('vid', userInfo.Usrid || '0');
        sessionStorage.setItem('mail', userInfo.mail || '');
        sessionStorage.setItem('vname', userInfo.Customer || '');

        setPage('products');
      } else {
        throw new Error('Invalid username or password.');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all user-related data from localStorage
    localStorage.removeItem('userName');
    localStorage.removeItem('customer');
    localStorage.removeItem('cusAddress');
    localStorage.removeItem('mail');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('vcode');
    localStorage.removeItem('vname');
    localStorage.removeItem('vid');
    localStorage.removeItem('accno');
    localStorage.removeItem('bankName');
    localStorage.removeItem('bid');
    localStorage.removeItem('branch');
    localStorage.removeItem('ifscode');
    localStorage.removeItem('qrcodelink');
    
    // Clear page from localStorage and sessionStorage
    localStorage.removeItem('page');
    sessionStorage.removeItem('page');
    localStorage.removeItem('cart');
    sessionStorage.removeItem('vid');
    sessionStorage.removeItem('mail');
    sessionStorage.removeItem('vname');
    
    setUser(null);
    setBank(null);
    setQr(null);
    setCart([]);
    setPage('home');
  };

  const handleCompleteOrder = () => {
    //  console.log('Order Completed:', { user, cart, total: cartContextValue.totalPrice });
    clearCart();
    setPage('confirmation');
  };

  const renderPage = () => {
    if (typeof page === 'object' && page.name === 'forgot') {
      if (!page.email) {
        return <ForgotPasswordEmailForm setPage={setPage} />;
      }
      return <EmailVerificationPage email={page.email} setPage={setPage} mode="reset" />;
    }

    switch (page) {
      case 'home':
        return <HomePage setPage={setPage} />;
      case 'register':
        return <RegistrationPage onRegister={handleRegister} setPage={setPage} />;
      case 'verify-email':
        return <EmailVerificationPage email={userEmail} setPage={setPage} />;
      case 'products':
        return <ProductListPage user={user} setPage={setPage}  />;
      case 'cart':
        return <ShoppingCartPage setPage={setPage} />;
      case 'order':
        return <OrderPage setPage={setPage} onCompleteOrder={handleCompleteOrder} />;
      case 'confirmation':
        return <OrderConfirmationPage setPage={setPage} />;
          case 'profile':
      return <ProfilePage user={user} setPage={setPage} onLogout={handleLogout}/>;

      case 'order-history':
  return <OrderHistoryPage user={user} setPage={setPage} />;

      
      case 'login':
      default:
        return <LoginPage onLogin={handleLogin} setPage={setPage} />;
    }
  };

  return (
    <CartContext.Provider value={cartContextValue}>
      <div className="min-h-screen font-sans text-gray-800 bg-gray-50">
        <Navbar user={user} setPage={setPage} onLogout={handleLogout} />
        <main className="p-4 sm:p-6 lg:p-8">
          {renderPage()}
        </main>
      </div>
    </CartContext.Provider>
  );
}
